// @flow
import React, { useState, useRef } from "react";
import { Text } from "@unifize/sarah";
import i18n from "i18next";
import k from "src/i18n/keys";

import {
  NewRoom as StyledNewRoom,
  Button as StyledButton,
  DropDownButton as StyledDropDownButton,
} from "./styles";

import Options from "src/containers/chatroom/NewRoom/Options";
import AddChatroom from "src/containers/chatroom/creator";
import Icons from "src/icons";
import Tooltip from "src/components/Tooltip";

import type { WorkflowId, SystemProcess } from "src/types";

interface Props {
  filter: {
    name: string;
    workflow?: WorkflowId;
    type: string[];
  };
  systemProcess: SystemProcess;
  setConversationAttributes: (attributes: {
    type: string | null;
    analytics: { location: string };
  }) => void;
  clearConversationDialog: () => void;
  setNewWorkflow: (workflow: WorkflowId) => void;
  chatroomCreationEnabled: boolean;
}

const NewRoom = ({
  filter,
  systemProcess,
  setConversationAttributes,
  clearConversationDialog,
  setNewWorkflow,
  chatroomCreationEnabled,
}: Props) => {
  const [addNewRoom, setAddNewRoom] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowId | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);

  const toggleNew = () => {
    setAddNewRoom((prev) => !prev);
    setWorkflow(null);
  };

  const toggleOptions = () => {
    setShowOptions((prev) => !prev);
  };

  const handleNew = () => {
    if (filter.name !== "My Inbox" && filter.workflow) {
      setModalType("workflow", filter.workflow);
    } else {
      setShowOptions((prev) => !prev);
    }
  };

  const setModalType = (
    type: string | null = null,
    workflow: WorkflowId | null = null,
  ) => {
    clearConversationDialog();

    const isSystemTask = systemProcess?.task;

    let newType = type === "task" && isSystemTask ? "workflow" : type;
    let newWorkflow = workflow;

    if (type === "task" && isSystemTask) {
      const taskId = systemProcess.task.toString();
      setNewWorkflow(taskId);
      newWorkflow = taskId;
    } else if (workflow) {
      setNewWorkflow(workflow);
      newWorkflow = workflow;
    }

    setConversationAttributes({
      type: newType,
      analytics: {
        location: "main-plus",
      },
    });

    setWorkflow(newWorkflow);
    setShowOptions(false);
    setAddNewRoom(true);
  };

  const getType = (type: string[], workflow: WorkflowId | null) => {
    if (workflow === null) {
      return type.length === 1 ? type[0] : null;
    }
    return "workflow";
  };

  const getModalType = (type: string[], workflow: WorkflowId | null) => {
    if (workflow !== null) {
      return "workflow";
    }

    if (type !== null) {
      return type;
    }

    return getType(type, workflow);
  };

  if (!chatroomCreationEnabled) {
    return null;
  }

  const newConversationButton = (
    <StyledButton
      type="button"
      data-cy="newConversation"
      onClick={handleNew}
      rounded="left"
    >
      <Icons type="hollowPlus" />
      <Text color="white">{i18n.t(k.NEW1)}</Text>
    </StyledButton>
  );

  return (
    <>
      <StyledNewRoom ref={outerRef}>
        {filter.name !== "My Inbox" && filter.workflow ? (
          <Tooltip placement="top" title={filter.name}>
            {newConversationButton}
          </Tooltip>
        ) : (
          newConversationButton
        )}
        <StyledDropDownButton
          rouded="right"
          type="button"
          onClick={toggleOptions}
        >
          <Icons type="keyboardArrowDown" />
        </StyledDropDownButton>
        {showOptions ? (
          <Options
            handleClose={toggleOptions}
            outerRef={outerRef.current}
            type={getType(filter.type, filter.workflow || null)}
            workflow={filter.workflow}
            setModalType={setModalType}
          />
        ) : null}
      </StyledNewRoom>

      {addNewRoom ? (
        <AddChatroom
          position="left"
          modalType={getModalType(filter.type, filter.workflow || null)}
          show={addNewRoom}
          handleClose={toggleNew}
          settings={{
            workflow: workflow !== null ? workflow : filter.workflow,
          }}
        />
      ) : null}
    </>
  );
};

export default NewRoom;
